import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import { portfolioData } from './data/portfolioData';
import ThreeBackground from './components/ThreeBackground';
import TiltCard from './components/TiltCard';
import CvModal from './components/CvModal';
import ProjectModal from './components/ProjectModal';
import CyberTerminal from './components/CyberTerminal';
import CyberAssistant from './components/CyberAssistant';
import { sound } from './components/SoundSystem';

function App() {
  const [activeSection, setActiveSection] = useState('accueil');
  const [activeSkillCategory, setActiveSkillCategory] = useState('all');
  const [activeProjectCategory, setActiveProjectCategory] = useState('all');
  const [activeTimelineTab, setActiveTimelineTab] = useState('experiences');
  const [activeStoryTab, setActiveStoryTab] = useState('vision');
  
  // Modals
  const [isCvModalOpen, setIsCvModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  
  // Sound state
  const [isSoundOn, setIsSoundOn] = useState(false);

  // Typewriter
  const [typewriterIndex, setTypewriterIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Mouse & Scroll
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [isHoveredInteractive, setIsHoveredInteractive] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [copiedKey, setCopiedKey] = useState(null);

  // Contact Form
  const [formState, setFormState] = useState({ name: '', email: '', subject: '', message: '' });
  const [formSent, setFormSent] = useState(false);

  const sectionsRef = {
    accueil: useRef(null),
    apropos: useRef(null),
    competences: useRef(null),
    projets: useRef(null),
    parcours: useRef(null),
    cv: useRef(null),
    contact: useRef(null)
  };

  // Mouse move listener
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
      setCursorPos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Scroll listener for active section & back to top
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 350);
      const scrollPos = window.scrollY + 180;

      Object.entries(sectionsRef).forEach(([key, ref]) => {
        if (ref.current) {
          const { offsetTop, offsetHeight } = ref.current;
          if (scrollPos >= offsetTop && scrollPos < offsetTop + offsetHeight) {
            setActiveSection(key);
          }
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Typewriter effect
  useEffect(() => {
    const subtitles = portfolioData.personal.subtitles;
    const currentFull = subtitles[typewriterIndex];
    let timer;

    if (!isDeleting) {
      if (displayText.length < currentFull.length) {
        timer = setTimeout(() => {
          setDisplayText(currentFull.slice(0, displayText.length + 1));
        }, 80);
      } else {
        timer = setTimeout(() => setIsDeleting(true), 2200);
      }
    } else {
      if (displayText.length > 0) {
        timer = setTimeout(() => {
          setDisplayText(currentFull.slice(0, displayText.length - 1));
        }, 40);
      } else {
        setIsDeleting(false);
        setTypewriterIndex((prev) => (prev + 1) % subtitles.length);
      }
    }

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, typewriterIndex]);

  const scrollToSection = (sec) => {
    sound.playClick();
    if (sectionsRef[sec]?.current) {
      sectionsRef[sec].current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleSoundState = () => {
    const newState = sound.toggleSound();
    setIsSoundOn(newState);
  };

  const handleDownloadCv = (e) => {
    sound.playSuccess();
    if (window.confetti) {
      window.confetti({
        particleCount: 120,
        spread: 80,
        origin: { y: 0.6 },
        colors: ['#00F59B', '#00E5FF', '#8B5CF6']
      });
    }
  };

  const copyToClipboard = (text, key) => {
    sound.playClick();
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2500);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    sound.playSuccess();
    if (window.confetti) {
      window.confetti({
        particleCount: 150,
        spread: 90,
        origin: { y: 0.5 },
        colors: ['#00F59B', '#00E5FF', '#8B5CF6', '#FFFFFF']
      });
    }
    setFormSent(true);
    setTimeout(() => {
      setFormSent(false);
      setFormState({ name: '', email: '', subject: '', message: '' });
    }, 4000);
  };

  // Filter skills
  const filteredSkills = activeSkillCategory === 'all'
    ? portfolioData.skills
    : portfolioData.skills.filter((s) => s.category === activeSkillCategory);

  // Filter projects
  const filteredProjects = activeProjectCategory === 'all'
    ? portfolioData.projects
    : portfolioData.projects.filter((p) => p.category === activeProjectCategory);

  return (
    <div className="portfolio-app-root">
      {/* 3D WebGL Background Scene */}
      <ThreeBackground mousePosition={mousePos} />

      {/* Futuristic Mesh & Grid Overlay */}
      <div className="cyber-grid-overlay"></div>
      <div className="cyber-ambient-glow"></div>

      {/* Custom High-Tech Glow Cursor (desktop) */}
      <div
        className={`cyber-custom-cursor ${isHoveredInteractive ? 'expanded' : ''}`}
        style={{ left: `${cursorPos.x}px`, top: `${cursorPos.y}px` }}
      />
      <div
        className="cyber-cursor-dot"
        style={{ left: `${cursorPos.x}px`, top: `${cursorPos.y}px` }}
      />

      {/* Floating Interactive Robot Assistant with QR Code */}
      <CyberAssistant />

      {/* Top Navbar */}
      <header className="cyber-header">
        <div className="header-container">
          <a href="#accueil" className="brand-logo" onClick={(e) => { e.preventDefault(); scrollToSection('accueil'); }}>
            <div className="brand-symbol">
              <span className="brand-bracket">&lt;</span>
              <span className="brand-initials">BD</span>
              <span className="brand-bracket">/&gt;</span>
            </div>
            <div className="brand-text">
              <span className="brand-name">Babacar Diagne</span>
              <span className="brand-badge">Ingénieur Logiciel</span>
            </div>
          </a>

          <nav className="header-nav">
            {[
              { id: 'accueil', label: 'Accueil' },
              { id: 'apropos', label: 'À Propos' },
              { id: 'competences', label: 'Compétences' },
              { id: 'projets', label: 'Projets 3D' },
              { id: 'parcours', label: 'Parcours' },
              { id: 'cv', label: 'CV Exécutif' },
              { id: 'contact', label: 'Contact' }
            ].map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={`nav-link-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.id);
                }}
                onMouseEnter={() => sound.playHover()}
              >
                <span className="nav-indicator-dot"></span>
                {item.label}
              </a>
            ))}
          </nav>

          <div className="header-right-tools">
            {/* Audio Toggle */}
            <button
              className={`sound-toggle-btn ${isSoundOn ? 'sound-active' : ''}`}
              onClick={toggleSoundState}
              title={isSoundOn ? 'Désactiver les effets audio' : 'Activer les effets sonores synthétiques'}
            >
              <div className="sound-bars">
                <span className="sbar bar1"></span>
                <span className="sbar bar2"></span>
                <span className="sbar bar3"></span>
                <span className="sbar bar4"></span>
              </div>
              <span className="sound-label">{isSoundOn ? 'AUDIO ON' : 'AUDIO OFF'}</span>
            </button>

            {/* Quick CV Download in Navbar */}
            <a
              href={portfolioData.personal.cvPdf}
              download="CV-BABACAR-DIAGNE-2026.pdf"
              className="navbar-cv-btn"
              onClick={handleDownloadCv}
              onMouseEnter={() => sound.playHover()}
            >
              <i className="fa-solid fa-file-arrow-down"></i>
              <span>CV PDF</span>
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="portfolio-main-content">
        {/* ============================================================== */}
        {/* SECTION 1: HERO SECTION */}
        {/* ============================================================== */}
        <section id="accueil" className="hero-section" ref={sectionsRef.accueil}>
          <div className="container hero-container">
            <div className="row align-items-center">
              {/* Left Column: Introduction & CTAs */}
              <div className="col-lg-7 hero-text-col">
                <div className="hero-status-pill">
                  <span className="status-live-beacon"></span>
                  <span className="status-text">{portfolioData.personal.status}</span>
                </div>

                <h1 className="hero-title">
                  <span className="hero-greeting">Bonjour, je suis</span>
                  <span className="hero-name-gradient">{portfolioData.personal.name}</span>
                </h1>

                <div className="hero-typewriter-box">
                  <span className="terminal-prefix">&gt;</span>
                  <span className="terminal-typing-text">{displayText}</span>
                  <span className="terminal-blinking-cursor">_</span>
                </div>

                <p className="hero-description">
                  {portfolioData.personal.bio}
                </p>

                {/* Hero Action Buttons */}
                <div className="hero-actions-row">
                  <button
                    className="cyber-cta-btn primary glow-btn"
                    onClick={() => {
                      sound.playClick();
                      setIsCvModalOpen(true);
                    }}
                    onMouseEnter={() => sound.playHover()}
                  >
                    <i className="fa-solid fa-eye"></i>
                    <span>Visualiser mon CV HD</span>
                  </button>

                  <a
                    href={portfolioData.personal.cvPdf}
                    download="CV-BABACAR-DIAGNE-2026.pdf"
                    className="cyber-cta-btn secondary"
                    onClick={handleDownloadCv}
                    onMouseEnter={() => sound.playHover()}
                  >
                    <i className="fa-solid fa-download"></i>
                    <span>Télécharger CV PDF</span>
                  </a>

                  <button
                    className="cyber-cta-btn outline"
                    onClick={() => scrollToSection('projets')}
                    onMouseEnter={() => sound.playHover()}
                  >
                    <i className="fa-solid fa-cubes"></i>
                    <span>Explorer Projets</span>
                  </button>
                </div>

                {/* Key Metrics Stats Row */}
                <div className="hero-stats-grid">
                  {portfolioData.personal.stats.map((st, idx) => (
                    <TiltCard key={idx} className="stat-card-tilt" maxTilt={10}>
                      <div className="stat-icon-wrapper">
                        <i className={st.icon}></i>
                      </div>
                      <div className="stat-number">{st.value}</div>
                      <div className="stat-caption">{st.label}</div>
                    </TiltCard>
                  ))}
                </div>
              </div>

              {/* Right Column: Extraordinary 3D Holographic Avatar Showcase */}
              <div className="col-lg-5 hero-avatar-col">
                <div className="holographic-avatar-wrapper">
                  <TiltCard className="avatar-tilt-frame" maxTilt={14}>
                    {/* Rotating Orbital Rings */}
                    <div className="orbital-ring ring-outer"></div>
                    <div className="orbital-ring ring-middle"></div>
                    <div className="orbital-ring ring-inner"></div>

                    {/* HUD Corner Tech Brackets */}
                    <div className="hud-corner top-left"></div>
                    <div className="hud-corner top-right"></div>
                    <div className="hud-corner bottom-left"></div>
                    <div className="hud-corner bottom-right"></div>

                    {/* Floating Orbiting Tech Badges */}
                    <div className="orbit-badge badge-java">
                      <i className="fa-brands fa-java"></i>
                      <span>Java</span>
                    </div>
                    <div className="orbit-badge badge-spring">
                      <i className="fa-solid fa-leaf"></i>
                      <span>Spring Boot</span>
                    </div>
                    <div className="orbit-badge badge-react">
                      <i className="fa-brands fa-react"></i>
                      <span>React</span>
                    </div>
                    <div className="orbit-badge badge-aws">
                      <i className="fa-brands fa-aws"></i>
                      <span>AWS</span>
                    </div>
                    <div className="orbit-badge badge-docker">
                      <i className="fa-brands fa-docker"></i>
                      <span>Docker</span>
                    </div>

                    {/* Main Image Container */}
                    <div className="avatar-img-circle">
                      <img
                        src="/about-me-1.png"
                        alt="Babacar Diagne"
                        className="avatar-core-img"
                      />
                      <div className="avatar-scan-overlay"></div>
                      <div className="avatar-chromatic-glow"></div>
                    </div>

                    {/* Diagnostics HUD Tag */}
                    <div className="avatar-hud-footer">
                      <div className="hud-meta-item">
                        <span className="dot-green"></span>
                        <span>DEV_ID: #BD96</span>
                      </div>
                      <div className="hud-meta-item">
                        <i className="fa-solid fa-location-dot"></i>
                        <span>SAINT-LOUIS, SN</span>
                      </div>
                    </div>
                  </TiltCard>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll Down Indicator */}
          <div className="hero-scroll-indicator" onClick={() => scrollToSection('apropos')}>
            <span className="scroll-mouse">
              <span className="scroll-wheel"></span>
            </span>
            <span className="scroll-caption">DÉCOUVRIR</span>
          </div>
        </section>

        {/* ============================================================== */}
        {/* SECTION 2: À PROPOS & VISION */}
        {/* ============================================================== */}
        <section id="apropos" className="section-padding apropos-section" ref={sectionsRef.apropos}>
          <div className="container">
            <div className="section-header-centered">
              <span className="section-kicker">
                <i className="fa-solid fa-fingerprint"></i> IDENTITÉ & IMPACT
              </span>
              <h2 className="section-main-title">
                Ingénieur Passionné, Bâtisseur de Solutions
              </h2>
              <p className="section-sub-desc">
                Cofondateur de startup et formateur d'ingénieurs, je place la rigueur architecturale et la créativité au cœur de chaque ligne de code.
              </p>
            </div>

            <div className="row g-4 align-items-stretch">
              {/* Left Column: Story & Tabs */}
              <div className="col-lg-6">
                <TiltCard className="story-card" maxTilt={6}>
                  <div className="story-tabs-nav">
                    {[
                      { id: 'vision', label: 'Vision & Entrepreneuriat', icon: 'fa-solid fa-lightbulb' },
                      { id: 'architecture', label: 'Architecture & Qualité', icon: 'fa-solid fa-cubes-stacked' },
                      { id: 'pedagogie', label: 'Transmission & UKB', icon: 'fa-solid fa-chalkboard-user' }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        className={`story-tab-btn ${activeStoryTab === tab.id ? 'active' : ''}`}
                        onClick={() => {
                          sound.playClick();
                          setActiveStoryTab(tab.id);
                        }}
                      >
                        <i className={tab.icon}></i>
                        <span>{tab.label}</span>
                      </button>
                    ))}
                  </div>

                  <div className="story-tab-content">
                    {activeStoryTab === 'vision' && (
                      <div className="story-body fade-in">
                        <h4>L'innovation technologique au service du réel</h4>
                        <p>
                          En cofondant la startup <strong>AppliCodeTech</strong>, mon ambition est claire : concevoir des plateformes pérennes et ergonomiques capables de résoudre des problématiques concrètes au Sénégal et à l'international.
                        </p>
                        <p>
                          De la modernisation du portail de la Direction de la Pêche Continentale (<strong>dpc.gouv.sn</strong>) à la surveillance côtière avec <strong>NauticaSenegal 2.0</strong>, j'allie maîtrise technique et orientation produit.
                        </p>
                        <div className="story-pills-row">
                          <span className="story-pill"><i className="fa-solid fa-check"></i> Co-fondateur AppliCodeTech</span>
                          <span className="story-pill"><i className="fa-solid fa-check"></i> Projets d'État & Privés</span>
                          <span className="story-pill"><i className="fa-solid fa-check"></i> Culture Agile & Scrum</span>
                        </div>
                      </div>
                    )}

                    {activeStoryTab === 'architecture' && (
                      <div className="story-body fade-in">
                        <h4>Des architectures logicielles résilientes & scalables</h4>
                        <p>
                          Diplômé d'un <strong>Master 2 en Ingénierie Logicielle</strong> à l'UNCHK, je privilégie des architectures distribuées basées sur <strong>Spring Boot</strong>, la sécurisation des endpoints avec <strong>Spring Security / JWT</strong>, et des interfaces modernes réactives en <strong>React.js</strong> et <strong>Angular</strong>.
                        </p>
                        <p>
                          L'intégration de pipelines CI/CD (Jenkins, SonarQube, GitHub Actions) et la conteneurisation Docker garantissent un code propre, testé et déployé avec une fiabilité maximale.
                        </p>
                        <div className="story-pills-row">
                          <span className="story-pill"><i className="fa-solid fa-check"></i> Clean Architecture</span>
                          <span className="story-pill"><i className="fa-solid fa-check"></i> CI/CD & SonarQube</span>
                          <span className="story-pill"><i className="fa-solid fa-check"></i> Cloud AWS & VPS OVH</span>
                        </div>
                      </div>
                    )}

                    {activeStoryTab === 'pedagogie' && (
                      <div className="story-body fade-in">
                        <h4>Former la nouvelle génération d'ingénieurs</h4>
                        <p>
                          Enseignant à l'<strong>Université Kocc Barma (UKB) de Saint-Louis</strong> et Responsable de la <strong>Cellule Interne d'Assurance Qualité (CIAQ)</strong>, j'enseigne les cours d'Algorithmique, Structures de données et Projets Opérationnels.
                        </p>
                        <p>
                          J'ai également conçu et déployé l'ERP de scolarité universitaire <strong>SGEP</strong>, automatisant la gestion de l'ensemble de l'établissement.
                        </p>
                        <div className="story-pills-row">
                          <span className="story-pill"><i className="fa-solid fa-check"></i> Formateur L1 & L3 Génie Informatique</span>
                          <span className="story-pill"><i className="fa-solid fa-check"></i> Responsable CIAQ UKB</span>
                          <span className="story-pill"><i className="fa-solid fa-check"></i> ERP SGEP en Production</span>
                        </div>
                      </div>
                    )}
                  </div>
                </TiltCard>
              </div>

              {/* Right Column: Interactive Cyber Terminal */}
              <div className="col-lg-6">
                <CyberTerminal onOpenCv={() => setIsCvModalOpen(true)} />
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================== */}
        {/* SECTION 3: COMPÉTENCES & ÉCOSYSTÈME */}
        {/* ============================================================== */}
        <section id="competences" className="section-padding competences-section" ref={sectionsRef.competences}>
          <div className="container">
            <div className="section-header-centered">
              <span className="section-kicker">
                <i className="fa-solid fa-layer-group"></i> EXPERTISE & OUTILS
              </span>
              <h2 className="section-main-title">
                Écosystème Technologique de Pointe
              </h2>
              <p className="section-sub-desc">
                Un ensemble complet de compétences du backend robuste jusqu'au frontend haute précision, en passant par le DevOps et le Cloud.
              </p>

              {/* Skills Filter Tabs */}
              <div className="category-filter-bar">
                {[
                  { id: 'all', label: 'Toutes les compétences' },
                  { id: 'backend', label: 'Backend & Java' },
                  { id: 'frontend', label: 'Frontend & Web' },
                  { id: 'cloud', label: 'Cloud & Database' },
                  { id: 'design', label: 'Design & UI/UX' },
                  { id: 'tools', label: 'DevOps & CMS' }
                ].map((cat) => (
                  <button
                    key={cat.id}
                    className={`filter-pill-btn ${activeSkillCategory === cat.id ? 'active' : ''}`}
                    onClick={() => {
                      sound.playClick();
                      setActiveSkillCategory(cat.id);
                    }}
                    onMouseEnter={() => sound.playHover()}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Skills Grid */}
            <div className="row g-3 skills-cards-grid">
              {filteredSkills.map((skill, index) => (
                <div key={index} className="col-lg-3 col-md-4 col-sm-6">
                  <TiltCard className="cyber-skill-card" maxTilt={10}>
                    <div className="skill-card-top">
                      <div className="skill-icon-bubble" style={{ color: skill.color }}>
                        <i className={skill.icon}></i>
                      </div>
                      <span className="skill-percent-badge">{skill.level}%</span>
                    </div>

                    <h4 className="skill-title">{skill.name}</h4>
                    <span className="skill-tagline">{skill.tag}</span>

                    {/* Progress Bar */}
                    <div className="skill-progress-track">
                      <div
                        className="skill-progress-bar"
                        style={{
                          width: `${skill.level}%`,
                          backgroundColor: skill.color,
                          boxShadow: `0 0 12px ${skill.color}88`
                        }}
                      />
                    </div>
                  </TiltCard>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================================== */}
        {/* SECTION 4: PROJETS HORS NORME SHOWCASE */}
        {/* ============================================================== */}
        <section id="projets" className="section-padding projets-section" ref={sectionsRef.projets}>
          <div className="container">
            <div className="section-header-centered">
              <span className="section-kicker">
                <i className="fa-solid fa-rocket"></i> RÉALISATIONS CONCRÈTES
              </span>
              <h2 className="section-main-title">
                Projets & Architectures Déployées
              </h2>
              <p className="section-sub-desc">
                Des applications étatiques, des ERPs universitaires, des systèmes de surveillance maritime et des plateformes startups à fort trafic.
              </p>

              {/* Projects Filter */}
              <div className="category-filter-bar">
                {[
                  { id: 'all', label: 'Tous les projets' },
                  { id: 'production', label: 'En Production' },
                  { id: 'critical', label: 'Systèmes Critiques' },
                  { id: 'startup', label: 'Startups & Entrepreneuriat' },
                  { id: 'research', label: 'Recherche Master' }
                ].map((pcat) => (
                  <button
                    key={pcat.id}
                    className={`filter-pill-btn ${activeProjectCategory === pcat.id ? 'active' : ''}`}
                    onClick={() => {
                      sound.playClick();
                      setActiveProjectCategory(pcat.id);
                    }}
                    onMouseEnter={() => sound.playHover()}
                  >
                    {pcat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Projects Grid */}
            <div className="row g-4">
              {filteredProjects.map((proj) => (
                <div key={proj.id} className="col-lg-6">
                  <TiltCard className="extraordinary-project-card" maxTilt={8}>
                    {/* Project Image Banner with 3D Holographic Treatment */}
                    <div
                      className="project-card-image-box"
                      onClick={() => {
                        sound.playClick();
                        setSelectedProject(proj);
                      }}
                    >
                      <img src={proj.image} alt={proj.title} className="project-banner-img" />
                      <div className="banner-hologram-shimmer"></div>
                      <div className="banner-scanline"></div>

                      {/* Status Badge */}
                      <div className="project-live-tag" style={{ borderColor: proj.statusColor }}>
                        <span className="live-dot" style={{ backgroundColor: proj.statusColor }}></span>
                        {proj.status}
                      </div>

                      {/* Hover Overlay Inspect Trigger */}
                      <div className="image-hover-inspect">
                        <i className="fa-solid fa-expand"></i>
                        <span>Inspecter en 3D / HD</span>
                      </div>
                    </div>

                    {/* Project Body */}
                    <div className="project-card-body">
                      <div className="project-header-meta">
                        <span className="project-category-label">{proj.categoryLabel}</span>
                        {proj.link && (
                          <a
                            href={proj.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-external-link"
                            title="Ouvrir le site en direct"
                            onClick={(e) => {
                              e.stopPropagation();
                              sound.playClick();
                            }}
                          >
                            <i className="fa-solid fa-arrow-up-right-from-square"></i>
                          </a>
                        )}
                      </div>

                      <h3
                        className="project-headline"
                        onClick={() => {
                          sound.playClick();
                          setSelectedProject(proj);
                        }}
                      >
                        {proj.title}
                      </h3>

                      <p className="project-summary-text">{proj.description}</p>

                      {/* Tech Chips */}
                      <div className="project-tags-row">
                        {proj.tags.map((tg, i) => (
                          <span key={i} className="project-tech-chip">
                            {tg}
                          </span>
                        ))}
                      </div>

                      {/* Card Action Buttons */}
                      <div className="project-card-actions">
                        <button
                          className="project-btn-inspect"
                          onClick={() => {
                            sound.playClick();
                            setSelectedProject(proj);
                          }}
                        >
                          <i className="fa-solid fa-circle-info"></i>
                          <span>Détails & Architecture</span>
                        </button>

                        {proj.link && (
                          <a
                            href={proj.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-btn-live"
                            onClick={() => sound.playClick()}
                          >
                            <i className="fa-solid fa-globe"></i>
                            <span>Voir le site</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </TiltCard>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============================================================== */}
        {/* SECTION 5: PARCOURS & FORMATIONS TIMELINE */}
        {/* ============================================================== */}
        <section id="parcours" className="section-padding parcours-section" ref={sectionsRef.parcours}>
          <div className="container">
            <div className="section-header-centered">
              <span className="section-kicker">
                <i className="fa-solid fa-timeline"></i> TIMELINE PROFESSIONNELLE
              </span>
              <h2 className="section-main-title">
                Parcours, Responsabilités & Diplômes
              </h2>
              <p className="section-sub-desc">
                Une trajectoire ascendante alliant recherche académique de haut niveau et leadership opérationnel sur le terrain.
              </p>

              {/* Timeline Mode Switcher */}
              <div className="timeline-switcher-bar">
                <button
                  className={`timeline-tab-btn ${activeTimelineTab === 'experiences' ? 'active' : ''}`}
                  onClick={() => {
                    sound.playClick();
                    setActiveTimelineTab('experiences');
                  }}
                >
                  <i className="fa-solid fa-briefcase"></i>
                  <span>Expériences Professionnelles</span>
                </button>
                <button
                  className={`timeline-tab-btn ${activeTimelineTab === 'education' ? 'active' : ''}`}
                  onClick={() => {
                    sound.playClick();
                    setActiveTimelineTab('education');
                  }}
                >
                  <i className="fa-solid fa-graduation-cap"></i>
                  <span>Diplômes & Formations</span>
                </button>
              </div>
            </div>

            {/* Timeline Tree */}
            <div className="cyber-timeline-wrapper">
              <div className="timeline-central-laser"></div>

              {activeTimelineTab === 'experiences' ? (
                <div className="timeline-items-container">
                  {portfolioData.experiences.map((exp, idx) => (
                    <div key={idx} className={`timeline-entry-row ${idx % 2 === 0 ? 'left' : 'right'}`}>
                      <div className="timeline-node-marker">
                        <div className="node-glow-ring"></div>
                        <div className="node-center-dot"></div>
                      </div>

                      <div className="timeline-card-col">
                        <TiltCard className="timeline-card" maxTilt={6}>
                          <div className="timeline-card-header">
                            <span className="period-badge">{exp.period}</span>
                            {exp.current && <span className="current-badge">Poste Actuel</span>}
                          </div>

                          <h3 className="timeline-role-title">{exp.role}</h3>
                          <div className="timeline-company-row">
                            <i className="fa-solid fa-building"></i>
                            <span className="company-name">{exp.company}</span>
                            <span className="company-location">• {exp.location}</span>
                          </div>

                          <p className="timeline-summary">{exp.description}</p>

                          <ul className="timeline-tasks-list">
                            {exp.tasks.map((task, tidx) => (
                              <li key={tidx}>
                                <i className="fa-solid fa-circle-check"></i>
                                <span>{task}</span>
                              </li>
                            ))}
                          </ul>

                          <div className="timeline-tech-footer">
                            <span className="tech-label">Stack :</span>
                            <span className="tech-values">{exp.tech}</span>
                          </div>
                        </TiltCard>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="timeline-items-container">
                  {portfolioData.education.map((edu, idx) => (
                    <div key={idx} className={`timeline-entry-row ${idx % 2 === 0 ? 'left' : 'right'}`}>
                      <div className="timeline-node-marker">
                        <div className="node-glow-ring"></div>
                        <div className="node-center-dot"></div>
                      </div>

                      <div className="timeline-card-col">
                        <TiltCard className="timeline-card" maxTilt={6}>
                          <div className="timeline-card-header">
                            <span className="period-badge">{edu.period}</span>
                          </div>

                          <h3 className="timeline-role-title">{edu.degree}</h3>
                          <div className="timeline-company-row">
                            <i className={edu.icon}></i>
                            <span className="company-name">{edu.institution}</span>
                            <span className="company-location">• {edu.location}</span>
                          </div>

                          <p className="timeline-summary">{edu.description}</p>
                        </TiltCard>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ============================================================== */}
        {/* SECTION 6: CURRICULUM VITAE EXÉCUTIF HORS NORME */}
        {/* ============================================================== */}
        <section id="cv" className="section-padding cv-showcase-section" ref={sectionsRef.cv}>
          <div className="container">
            <div className="cv-banner-box">
              <div className="row align-items-center">
                {/* Left Description */}
                <div className="col-lg-6">
                  <span className="cv-section-tag">
                    <i className="fa-solid fa-award"></i> DOCUMENT EXÉCUTIF 2026
                  </span>
                  <h2 className="cv-section-title">
                    Curriculum Vitae d'Ingénieur Logiciel
                  </h2>
                  <p className="cv-section-desc">
                    Téléchargez un CV au design moderne, structuré pour les standards internationaux, conforme aux exigences ATS et valorisant l'ensemble de mes réalisations techniques et académiques.
                  </p>

                  <div className="cv-features-list">
                    <div className="cv-feat-item">
                      <i className="fa-solid fa-circle-check"></i>
                      <span>Format A4 PDF Haute Résolution (300 DPI)</span>
                    </div>
                    <div className="cv-feat-item">
                      <i className="fa-solid fa-circle-check"></i>
                      <span>2 Pages complètes : Expériences, Formations & Références</span>
                    </div>
                    <div className="cv-feat-item">
                      <i className="fa-solid fa-circle-check"></i>
                      <span>Liens directs vérifiables vers les plateformes en production</span>
                    </div>
                  </div>

                  <div className="cv-download-actions">
                    <a
                      href={portfolioData.personal.cvPdf}
                      download="CV-BABACAR-DIAGNE-2026.pdf"
                      className="cyber-cta-btn primary glow-btn"
                      onClick={handleDownloadCv}
                    >
                      <i className="fa-solid fa-cloud-arrow-down"></i>
                      <span>Télécharger le CV PDF</span>
                    </a>

                    <button
                      className="cyber-cta-btn secondary"
                      onClick={() => {
                        sound.playClick();
                        setIsCvModalOpen(true);
                      }}
                    >
                      <i className="fa-solid fa-expand"></i>
                      <span>Visualiser en Plein Écran</span>
                    </button>
                  </div>
                </div>

                {/* Right Interactive Dual-Page Preview */}
                <div className="col-lg-6">
                  <div
                    className="cv-3d-mockup-wrapper"
                    onClick={() => {
                      sound.playClick();
                      setIsCvModalOpen(true);
                    }}
                  >
                    <TiltCard className="cv-mockup-card page-1" maxTilt={12}>
                      <img
                        src="/assets/cv-page-1.png"
                        alt="CV Babacar Diagne Page 1"
                        className="cv-mock-page"
                      />
                      <div className="mockup-label">Page 1 — Profil & Expériences</div>
                    </TiltCard>

                    <TiltCard className="cv-mockup-card page-2" maxTilt={12}>
                      <img
                        src="/assets/cv-page-2.png"
                        alt="CV Babacar Diagne Page 2"
                        className="cv-mock-page"
                      />
                      <div className="mockup-label">Page 2 — Formations & Références</div>
                    </TiltCard>

                    <div className="mockup-click-hint">
                      <i className="fa-solid fa-magnifying-glass-plus"></i>
                      <span>Cliquer pour inspecter en HD</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============================================================== */}
        {/* SECTION 7: CONTACT & COLLABORATION */}
        {/* ============================================================== */}
        <section id="contact" className="section-padding contact-section" ref={sectionsRef.contact}>
          <div className="container">
            <div className="section-header-centered">
              <span className="section-kicker">
                <i className="fa-solid fa-paper-plane"></i> ENTRER EN CONTACT
              </span>
              <h2 className="section-main-title">
                Concrétisons Vos Projets
              </h2>
              <p className="section-sub-desc">
                Vous avez un projet innovant, un besoin d'expertise Full Stack ou une opportunité Lead Tech ? Discutons-en dès aujourd'hui.
              </p>
            </div>

            <div className="row g-4">
              {/* Contact Information & Channels */}
              <div className="col-lg-5">
                <div className="contact-info-cards-stack">
                  {/* Phone */}
                  <TiltCard className="contact-channel-card" maxTilt={6}>
                    <div className="channel-icon-bubble phone">
                      <i className="fa-solid fa-phone"></i>
                    </div>
                    <div className="channel-text-content">
                      <span className="channel-label">Téléphone & WhatsApp</span>
                      <a href={`tel:${portfolioData.personal.phone.replace(/\s+/g, '')}`} className="channel-value">
                        {portfolioData.personal.phone}
                      </a>
                    </div>
                    <button
                      className="channel-copy-btn"
                      onClick={() => copyToClipboard(portfolioData.personal.phone, 'phone')}
                      title="Copier le numéro"
                    >
                      <i className={`fa-solid ${copiedKey === 'phone' ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                  </TiltCard>

                  {/* Email */}
                  <TiltCard className="contact-channel-card" maxTilt={6}>
                    <div className="channel-icon-bubble email">
                      <i className="fa-solid fa-envelope"></i>
                    </div>
                    <div className="channel-text-content">
                      <span className="channel-label">Email Professionnel</span>
                      <a href={`mailto:${portfolioData.personal.email}`} className="channel-value">
                        {portfolioData.personal.email}
                      </a>
                    </div>
                    <button
                      className="channel-copy-btn"
                      onClick={() => copyToClipboard(portfolioData.personal.email, 'email')}
                      title="Copier l'email"
                    >
                      <i className={`fa-solid ${copiedKey === 'email' ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                  </TiltCard>

                  {/* Location */}
                  <TiltCard className="contact-channel-card" maxTilt={6}>
                    <div className="channel-icon-bubble location">
                      <i className="fa-solid fa-location-dot"></i>
                    </div>
                    <div className="channel-text-content">
                      <span className="channel-label">Localisation</span>
                      <span className="channel-value">{portfolioData.personal.location}</span>
                    </div>
                  </TiltCard>

                  {/* Direct WhatsApp Callout Banner */}
                  <div className="whatsapp-quick-banner">
                    <div className="wa-icon">
                      <i className="fa-brands fa-whatsapp"></i>
                    </div>
                    <div className="wa-content">
                      <h5>Discussion instantanée</h5>
                      <p>Disponible en direct sur WhatsApp pour échanger rapidement.</p>
                      <a
                        href={`https://wa.me/221773490318?text=${encodeURIComponent("Bonjour Babacar, je vous contacte suite à la visite de votre portfolio.")}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="wa-link-btn"
                        onClick={() => sound.playClick()}
                      >
                        Ouvrir WhatsApp (+221 77 349 03 18)
                        <i className="fa-solid fa-arrow-right"></i>
                      </a>
                    </div>
                  </div>

                  {/* Social Networks Row */}
                  <div className="social-links-deck">
                    <a
                      href={portfolioData.personal.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-pill-btn"
                      title="GitHub"
                    >
                      <i className="fa-brands fa-github"></i>
                      <span>GitHub</span>
                    </a>
                    <a
                      href={portfolioData.personal.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-pill-btn"
                      title="LinkedIn"
                    >
                      <i className="fa-brands fa-linkedin-in"></i>
                      <span>LinkedIn</span>
                    </a>
                    <a
                      href={portfolioData.personal.sololearn}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-pill-btn"
                      title="SoloLearn"
                    >
                      <i className="fa-solid fa-code"></i>
                      <span>SoloLearn</span>
                    </a>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="col-lg-7">
                <TiltCard className="cyber-form-card" maxTilt={4}>
                  <div className="form-card-header">
                    <span className="terminal-badge">SEND_MESSAGE.API</span>
                    <h3>Transmettre un message</h3>
                    <p>Remplissez le formulaire ci-dessous pour une réponse sous 24 heures.</p>
                  </div>

                  {formSent ? (
                    <div className="form-success-box">
                      <div className="success-icon-circle">
                        <i className="fa-solid fa-check"></i>
                      </div>
                      <h4>Message transmis avec succès !</h4>
                      <p>Merci pour votre prise de contact. Babacar Diagne vous répondra dans les plus brefs délais.</p>
                    </div>
                  ) : (
                    <form className="contact-form-grid" onSubmit={handleFormSubmit}>
                      <div className="form-group-item">
                        <label>
                          <span className="prompt-char">$</span> Nom & Prénom
                        </label>
                        <input
                          type="text"
                          required
                          value={formState.name}
                          onChange={(e) => setFormState({ ...formState, name: e.target.value })}
                          placeholder="Ex: Sophie Martin"
                          className="cyber-form-input"
                        />
                      </div>

                      <div className="form-group-item">
                        <label>
                          <span className="prompt-char">$</span> Adresse Email
                        </label>
                        <input
                          type="email"
                          required
                          value={formState.email}
                          onChange={(e) => setFormState({ ...formState, email: e.target.value })}
                          placeholder="Ex: contact@entreprise.com"
                          className="cyber-form-input"
                        />
                      </div>

                      <div className="form-group-item full-width">
                        <label>
                          <span className="prompt-char">$</span> Sujet du projet
                        </label>
                        <input
                          type="text"
                          required
                          value={formState.subject}
                          onChange={(e) => setFormState({ ...formState, subject: e.target.value })}
                          placeholder="Ex: Conception d'une plateforme web / Mission Lead Tech"
                          className="cyber-form-input"
                        />
                      </div>

                      <div className="form-group-item full-width">
                        <label>
                          <span className="prompt-char">$</span> Détails du message
                        </label>
                        <textarea
                          rows={5}
                          required
                          value={formState.message}
                          onChange={(e) => setFormState({ ...formState, message: e.target.value })}
                          placeholder="Décrivez brièvement vos attentes, calendrier et technologies envisagées..."
                          className="cyber-form-input"
                        />
                      </div>

                      <div className="form-submit-row full-width">
                        <button type="submit" className="cyber-cta-btn primary submit-btn">
                          <i className="fa-solid fa-paper-plane"></i>
                          <span>Envoyer le message</span>
                        </button>
                      </div>
                    </form>
                  )}
                </TiltCard>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="cyber-footer">
        <div className="container">
          <div className="footer-flex-row">
            <div className="footer-brand">
              <span className="footer-logo">&lt;BD/&gt; Babacar Diagne</span>
              <p className="footer-tagline">Ingénieur Logiciel Full Stack & Lead Tech — Sénégal</p>
            </div>

            <div className="footer-copy">
              <span>© {new Date().getFullYear()} Babacar Diagne. Tous droits réservés.</span>
              <span className="footer-tech-note">Propulsé par React, Three.js & Architecture Cyber-Chic</span>
            </div>

            <div className="footer-back-to-top">
              <button
                className="footer-top-btn"
                onClick={() => scrollToSection('accueil')}
                title="Retour en haut"
              >
                <i className="fa-solid fa-arrow-up"></i>
                <span>HAUT</span>
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Back to Top Floating Button */}
      <button
        className={`floating-top-btn ${showBackToTop ? 'visible' : ''}`}
        onClick={() => scrollToSection('accueil')}
        title="Remonter en haut de page"
      >
        <i className="fa-solid fa-chevron-up"></i>
      </button>

      {/* CV Modal Lightbox */}
      <CvModal isOpen={isCvModalOpen} onClose={() => setIsCvModalOpen(false)} />

      {/* Project 3D Lightbox Modal */}
      <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
    </div>
  );
}

export default App;
